import { BarChart, CustomChart, LineChart } from "echarts/charts";
import { GridComponent, TitleComponent, TooltipComponent } from "echarts/components";
import * as echarts from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Spinner } from "@/components/ui/spinner";
import { useDevicePixelRatio } from "@/hooks/use-device-pixel-ratio";
import type { BandData } from "../band-data/types";
import { AxisSide, type TimeAxisPanelSpec } from "../ChartSpec";
import { useHoverTime } from "../crosshair/use-hover-time";
import { useCrosshairBus, useDataViewerStore, useWidthGroupRegistry } from "../data-viewer-provider";
import type { StreamResolutions } from "../resolution/panel-resolution-slice";
import { pickStreamResolution } from "../resolution/pick-resolution";
import type { RequestedStream } from "../stream-data/types";
import { useEnsureStreamData } from "../stream-data/use-ensure-stream-data";
import { useStreamData } from "../stream-data/use-stream-data";
import { useGetDisplayInfo } from "../units/use-get-display-info";
import { BandOverlay } from "./band/band-overlay";
import { useBandHover, usePanelBand } from "./band/use-panel-band";
import { buildTimePanelOptions } from "./build-time-panel-options";
import { DataViewerPanelHeader } from "./header/data-viewer-panel-header";
import { hoverTagsFit } from "./hover-values/hover-tags-fit";
import { HoverValues } from "./hover-values/hover-values";
import { HEADER_TO_PLOT_PX, X_AXIS_BAND_PX } from "./layout/plot-geometry";
import { bandExtraHeightPx } from "./layout/plot-layout";
import { usePanelPlotLayout } from "./layout/use-panel-plot-layout";
import { CARD_BACKGROUND, RULE_COLOR } from "./panel-tokens";
import { ParkedCrosshair } from "./parked-crosshair";
import { type GetConversion, prepareSeries } from "./prepare-series";
import { PanelReadout } from "./readout/panel-readout";
import { PanelReadoutProvider } from "./readout/readout-context";
import { PANEL_WIDTH_GRIP_RESERVE_PX, ResizerGrip } from "./resize/resizer-grip";
import { usePanelResize } from "./resize/use-panel-resize";
import { useLeftYAxisSelection } from "./use-left-y-axis-selection";
import { useManagePanelFocus } from "./use-panel-focus";

const DEFAULT_PLOT_HEIGHT_PX = 150;
const DEFAULT_PANEL_ROW_GAP_PX = 4;
const MAX_HEIGHT_TO_WIDTH_RATIO = 0.55;

/** Chart card box. The plot gets the panel width, so the card adds its border and padding on top. */
const CARD_BORDER_PX = 1;
const PLOT_TO_DIVIDER_PX = 6;
const CARD_BOTTOM_PADDING_PX = 8;
/** Covers the plot only, so a band stays hoverable while stream data is missing or loading. */
const PLOT_MASK_CLASS = "absolute inset-x-0 flex items-center justify-center bg-background/60";
const CARD_PADDING_PX = 10;
const PLOT_INSET_IN_CARD_PX = CARD_BORDER_PX + CARD_PADDING_PX;

echarts.use([LineChart, BarChart, CustomChart, TitleComponent, TooltipComponent, GridComponent, CanvasRenderer]);

// Skip re-render when another panel's spec changes — this panel's props are unchanged.
export const Panel = memo(
    ({
        spec,
        groupId,
        maxStackWidth,
        syncCrosshair = true,
        emitsHoverTime = true,
        resizable = true,
        baseHeightPx,
        bandData,
    }: {
        spec: TimeAxisPanelSpec;
        /** Width group this panel shares gutters with — all panels in a stack pass the same id. */
        groupId: string;
        maxStackWidth: number;
        syncCrosshair?: boolean;
        emitsHoverTime?: boolean;
        resizable?: boolean;
        baseHeightPx?: number;
        bandData?: BandData;
    }) => {
        const startMs = useDataViewerStore((state) => state.startMs);
        const endMs = useDataViewerStore((state) => state.endMs);
        const panelBand = usePanelBand(spec, bandData, { startMs, endMs });

        const chartRef = useRef<HTMLDivElement | null>(null);
        const [chartInstance, setChartInstance] = useState<echarts.ECharts | null>(null);
        const crosshairBus = useCrosshairBus();
        const hoverTime = useHoverTime({ hasBand: panelBand !== undefined });
        const devicePixelRatio = useDevicePixelRatio();

        const gripReservePx = resizable ? PANEL_WIDTH_GRIP_RESERVE_PX + DEFAULT_PANEL_ROW_GAP_PX : 0;
        const maxWidth = Math.max(0, maxStackWidth - gripReservePx - PLOT_INSET_IN_CARD_PX * 2);
        const baseHeight = baseHeightPx ?? DEFAULT_PLOT_HEIGHT_PX + X_AXIS_BAND_PX;
        const widthCappedBaseHeight =
            maxWidth > 0 ? Math.min(baseHeight, Math.round(maxWidth * MAX_HEIGHT_TO_WIDTH_RATIO)) : baseHeight;
        const defaultHeight = widthCappedBaseHeight + bandExtraHeightPx(panelBand?.position);
        const { size, heightHandleProps, widthHandleProps } = usePanelResize({
            maxWidth,
            defaultHeight,
        });
        const forceRaw = useDataViewerStore((state) => state.debugForceRawStreams);

        // Strip display fields — fetch hooks only need streamId, its resolution and how to aggregate its buckets.
        const requestedStreams = useMemo<RequestedStream[]>(
            () =>
                spec.streams.map((stream) => ({
                    streamId: stream.streamId,
                    resolution: forceRaw ? "raw" : pickStreamResolution(stream.seriesType, size.width, startMs, endMs),
                    aggregate: stream.aggregate,
                })),
            [spec.streams, forceRaw, size.width, startMs, endMs],
        );

        const setPanelResolutions = useDataViewerStore((state) => state.setPanelResolutions);
        const clearPanelResolutions = useDataViewerStore((state) => state.clearPanelResolutions);
        const resolutionsKey = requestedStreams.map((stream) => `${stream.streamId}:${stream.resolution}`).join(",");
        useEffect(() => {
            const resolutions: StreamResolutions = Object.fromEntries(
                requestedStreams.map((stream) => [stream.streamId, stream.resolution]),
            );
            setPanelResolutions(groupId, spec.id, resolutions);
            return () => clearPanelResolutions(groupId, spec.id);
            // eslint-disable-next-line react-hooks/exhaustive-deps -- resolutionsKey stabilizes requestedStreams, which changes on every pan
        }, [groupId, spec.id, resolutionsKey, setPanelResolutions, clearPanelResolutions]);

        const getDisplayInfo = useGetDisplayInfo();
        const leftAxisSelection = useLeftYAxisSelection(spec.id, spec.axes);
        const focus = useManagePanelFocus(spec.id, spec.streams);
        const focusedStreamId = focus.focusedStream?.streamId;
        const focusedAxis = spec.axes.find((axis) => axis.id === focus.focusedStream?.axisId);
        // Focus borrows the left axis without touching the user's pick, so clearing it restores that pick.
        const activeLeftAxisId =
            focusedAxis?.side === AxisSide.Left ? focusedAxis.id : leftAxisSelection.activeLeftAxis?.id;

        // Kick off a fetch for any missing data; read windowed points from cache. See stream-data/README.md.
        const isFetching = useEnsureStreamData({ streams: requestedStreams, startMs, endMs });
        const data = useStreamData({ streams: requestedStreams, startMs, endMs });

        // Join cached points with spec (color, name, axis) and convert to display units.
        const preparedSeries = useMemo(() => {
            const getConversion: GetConversion = (unitCategory) => getDisplayInfo(unitCategory).fromDatabase;
            return prepareSeries(data, spec.streams, spec.axes, getConversion);
        }, [data, spec.streams, spec.axes, getDisplayInfo]);

        // Bar buckets with no average still come through as points, so count values, not points.
        const nothingDrawn = preparedSeries.every((series) => series.points.every(([, value]) => value === null));
        // Never covers drawn lines: added streams and pans load behind what's already there.
        const isLoadingStreamData = isFetching && nothingDrawn;

        // One layout drives both the echarts grid and the DOM overlays, so they can't drift apart.
        const registry = useWidthGroupRegistry();
        const { layout, axisScales } = usePanelPlotLayout({
            axes: spec.axes,
            streams: spec.streams,
            preparedSeries,
            activeLeftAxisId,
            panelHeightPx: size.height,
            groupId,
            memberId: spec.id,
            registry,
            bandPosition: panelBand?.position,
        });

        const plotBottomPx = Math.max(size.height - layout.plotBottomInsetPx, 0);
        const plotMaskStyle = { top: layout.plotTopPx, bottom: layout.plotBottomInsetPx };
        const plotHeightPx = Math.max(plotBottomPx - layout.plotTopPx, 0);
        const plotWidthPx = Math.max(size.width - layout.gutters.left - layout.gutters.right, 0);
        const bandHover = useBandHover(panelBand, { startMs, endMs }, plotWidthPx);

        // A focused panel gates on its one stream, whose unit the header names, so a bare number reads.
        const hoverTags = useMemo(
            () =>
                focusedAxis && focus.focusedStream
                    ? hoverTagsFit({ axes: [focusedAxis], streams: [focus.focusedStream], plotHeightPx })
                    : hoverTagsFit({ axes: spec.axes, streams: spec.streams, plotHeightPx }),
            [focusedAxis, focus.focusedStream, spec.axes, spec.streams, plotHeightPx],
        );

        const chartOptions = useMemo(
            () =>
                buildTimePanelOptions(
                    spec,
                    preparedSeries,
                    layout,
                    axisScales,
                    { startMs, endMs },
                    activeLeftAxisId,
                    panelBand,
                    bandHover.highlight,
                    focusedStreamId,
                ),
            [
                spec,
                preparedSeries,
                layout,
                axisScales,
                startMs,
                endMs,
                activeLeftAxisId,
                panelBand,
                bandHover.highlight,
                focusedStreamId,
            ],
        );

        // Ref so the init effect reads latest options without re-running.
        const chartOptionsRef = useRef(chartOptions);
        chartOptionsRef.current = chartOptions;

        // Init chart once on mount. Option updates go through setOption below.
        useLayoutEffect(() => {
            const chartContainer = chartRef.current;
            if (!chartContainer) return undefined;

            const existing = echarts.getInstanceByDom(chartContainer);
            if (existing) {
                crosshairBus.unregister(existing);
                existing.dispose();
            }

            // ECharts freezes the pixel ratio at init, so a change of screen re-runs this effect.
            const instance = echarts.init(chartContainer, undefined, { devicePixelRatio });
            instance.setOption(chartOptionsRef.current);

            if (syncCrosshair) crosshairBus.register(instance);
            if (emitsHoverTime) hoverTime.enable(instance);
            setChartInstance(instance);

            return () => {
                setChartInstance(null);
                hoverTime.disable();
                crosshairBus.unregister(instance);
                instance.dispose();
            };
        }, [syncCrosshair, emitsHoverTime, crosshairBus, hoverTime, devicePixelRatio]);

        useEffect(() => {
            if (chartRef.current) {
                echarts.getInstanceByDom(chartRef.current)?.resize();
            }
        }, [size.height, size.width]);

        // Push option changes without tearing down the chart instance.
        // replaceMerge: shrinking series, axis or grid lists must replace those arrays — default merge leaves orphans.
        //
        // useLayoutEffect so ECharts picks up gutter changes before the browser paints. useEffect would
        // flash the old (too narrow) grid for one frame when a sibling panel mounts and widens the stack.
        useLayoutEffect(() => {
            if (chartRef.current) {
                echarts.getInstanceByDom(chartRef.current)?.setOption(chartOptions, {
                    replaceMerge: ["series", "yAxis", "xAxis", "grid"],
                });
            }
        }, [chartOptions]);

        const cardWidthPx = size.width + PLOT_INSET_IN_CARD_PX * 2;

        const noDataForRange = requestedStreams.length > 0 && nothingDrawn;

        return (
            <div className={"w-full"}>
                <div className={"inline-flex max-w-full"}>
                    {/* items-start: centering this column would shift the height grip off the card's center. */}
                    <div className={"flex flex-col items-start gap-[2px]"}>
                        <div className={"flex shrink-0 items-center gap-[4px]"}>
                            <div
                                className={"flex shrink-0 flex-col rounded-[12px] border"}
                                style={{
                                    width: cardWidthPx,
                                    borderWidth: CARD_BORDER_PX,
                                    borderColor: RULE_COLOR,
                                    backgroundColor: CARD_BACKGROUND,
                                }}
                            >
                                <DataViewerPanelHeader
                                    axes={spec.axes}
                                    leftAxisSelection={leftAxisSelection}
                                    focusedAxis={focusedAxis}
                                />
                                <PanelReadoutProvider series={preparedSeries}>
                                    <div
                                        className={"flex flex-col"}
                                        style={{
                                            padding: `${HEADER_TO_PLOT_PX}px ${CARD_PADDING_PX}px ${CARD_BOTTOM_PADDING_PX}px`,
                                        }}
                                    >
                                        <div className={"relative shrink-0"} style={{ height: size.height }}>
                                            <div ref={chartRef} className={"size-full"} />

                                            <HoverValues
                                                chart={chartInstance}
                                                plotTopPx={layout.plotTopPx}
                                                plotBottomPx={plotBottomPx}
                                                panelWidthPx={size.width}
                                                hoverTags={hoverTags}
                                                focusedStreamId={focusedStreamId}
                                            />

                                            <ParkedCrosshair
                                                chart={chartInstance}
                                                layout={layout}
                                                panelWidthPx={size.width}
                                                plotBottomPx={plotBottomPx}
                                            />

                                            {panelBand && (
                                                <BandOverlay
                                                    band={panelBand}
                                                    hovered={bandHover.hovered}
                                                    chart={chartInstance}
                                                    layout={layout}
                                                    panelWidthPx={size.width}
                                                />
                                            )}

                                            {!isLoadingStreamData && noDataForRange && (
                                                <div className={PLOT_MASK_CLASS} style={plotMaskStyle}>
                                                    <div className={"text-sm text-muted-foreground"}>
                                                        No data for range
                                                    </div>
                                                </div>
                                            )}
                                            {isLoadingStreamData ? (
                                                <div className={PLOT_MASK_CLASS} style={plotMaskStyle}>
                                                    <Spinner className={"size-6 text-muted-foreground"} />
                                                </div>
                                            ) : null}
                                        </div>
                                        <div style={{ marginTop: PLOT_TO_DIVIDER_PX }}>
                                            <PanelReadout
                                                getDisplayInfo={getDisplayInfo}
                                                band={panelBand}
                                                focus={focus}
                                            />
                                        </div>
                                    </div>
                                </PanelReadoutProvider>
                            </div>
                            {resizable && <ResizerGrip orientation={"vertical"} handleProps={widthHandleProps} />}
                        </div>
                        {resizable && (
                            <div className={"flex justify-center"} style={{ width: cardWidthPx }}>
                                <ResizerGrip orientation={"horizontal"} handleProps={heightHandleProps} />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    },
);
Panel.displayName = "Panel";
